import { CheckCircle2, ChevronDown, CircleAlert, Clock3, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

interface FactsMeta {
  queryStart?: string
  queryEnd?: string
  exportedAt?: string
  generatedAt: string
  sourceFileName?: string
  sourceRows?: number | null
  productCount?: number | null
  variantCount?: number | null
  mappedVariantCount?: number | null
  unmappedVariantCount?: number | null
}

interface PromotionMeta {
  queryStart: string
  queryEnd: string
  generatedAt: string
}

interface ZeroSpendPeriod {
  startDate: string
  endDate: string
  source?: string
}

interface FreshnessPayload {
  generatedAt: string
  today: string
  orders: FactsMeta | null
  afterSales: FactsMeta | null
  promotion: PromotionMeta | null
  catalog: FactsMeta | null
  confirmedZeroSpendPeriods: ZeroSpendPeriod[]
}

const exportTimeText = (value?: string) => value ? value.replace('T', ' ').slice(0, 19) : '—'

const shanghaiToday = () => new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date())

const shiftIsoDate = (value: string, days: number) => {
  const date = new Date(`${value}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

function latestPeriod(periods: ZeroSpendPeriod[]): ZeroSpendPeriod | null {
  if (!periods.length) return null
  return periods.reduce((latest, period) => period.endDate > latest.endDate ? period : latest)
}

type UpdateStateKind = 'ready' | 'late' | 'short' | 'missing' | 'loading' | 'on-demand'

interface UpdateState {
  kind: UpdateStateKind
  label: string
  gapDays: number | null
}

function daysBetween(startDate: string, endDate: string) {
  return Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86400000)
}

function dailyUpdateState(
  meta: { queryStart?: string; queryEnd?: string } | null,
  expectedStart: string,
  expectedEnd: string,
  loading: boolean,
): UpdateState {
  if (loading && !meta) return { kind: 'loading', label: '核对中', gapDays: null }
  if (!meta?.queryStart || !meta.queryEnd) return { kind: 'missing', label: '未建立', gapDays: null }
  if (meta.queryEnd < expectedEnd) {
    const gapDays = Math.max(daysBetween(meta.queryEnd, expectedEnd), 0)
    return { kind: 'late', label: `滞后${gapDays}天`, gapDays }
  }
  if (meta.queryStart > expectedStart) return { kind: 'short', label: '区间不足', gapDays: 0 }
  return { kind: 'ready', label: '已覆盖', gapDays: 0 }
}

function UpdateStatusPill({ state }: { state: UpdateState }) {
  return <span className={`pdd-update-status is-${state.kind}`}>{state.label}</span>
}

interface UpdateTaskRowProps {
  index: number
  title: string
  fileLabel: string
  route: string
  dateRule: string
  range: string
  state: UpdateState
}

function UpdateTaskRow({ index, title, fileLabel, route, dateRule, range, state }: UpdateTaskRowProps) {
  return (
    <div className="pdd-update-task-row">
      <span className={`pdd-update-task-index is-${state.kind}`}>
        {state.kind === 'ready' ? <CheckCircle2 size={17} aria-hidden /> : index}
      </span>
      <div className="pdd-update-task-main">
        <div className="pdd-update-task-title">
          <strong>{title}</strong>
          <span>{fileLabel}</span>
        </div>
        <p>{route} · {dateRule}</p>
      </div>
      <div className="pdd-update-task-range">
        <span>本次范围</span>
        <strong>{range}</strong>
      </div>
      <UpdateStatusPill state={state} />
    </div>
  )
}

export function PinduoduoDataUploadPage() {
  const [freshness, setFreshness] = useState<FreshnessPayload | null>(null)
  const [freshnessError, setFreshnessError] = useState('')
  const [freshnessLoading, setFreshnessLoading] = useState(true)

  const loadFreshness = useCallback(async () => {
    setFreshnessLoading(true)
    setFreshnessError('')
    try {
      const response = await fetch('/api/pinduoduo/data-freshness', { cache: 'no-store' })
      const payload = await response.json() as FreshnessPayload & { error?: string }
      if (!response.ok) throw new Error(payload.error || `数据状态加载失败：${response.status}`)
      setFreshness(payload)
    } catch (reason) {
      setFreshnessError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setFreshnessLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadFreshness()
    // 仅在页面挂载时加载一次，之后可手动刷新。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const orders = freshness?.orders || null
  const afterSales = freshness?.afterSales || null
  const promotion = freshness?.promotion || null
  const catalog = freshness?.catalog || null
  const pausePeriod = latestPeriod(freshness?.confirmedZeroSpendPeriods || [])
  const today = freshness?.today || shanghaiToday()
  const exportEnd = shiftIsoDate(today, -1)
  const exportStart = shiftIsoDate(exportEnd, -89)
  const exportRange = `${exportStart} 00:00:00 — ${exportEnd} 23:59:59`
  const advertisingCoveredUntil = pausePeriod && promotion
    ? (pausePeriod.endDate > promotion.queryEnd ? pausePeriod.endDate : promotion.queryEnd)
    : pausePeriod?.endDate || promotion?.queryEnd || ''
  const orderState = dailyUpdateState(orders, exportStart, exportEnd, freshnessLoading)
  const afterSalesState = dailyUpdateState(afterSales, exportStart, exportEnd, freshnessLoading)
  const promotionState = dailyUpdateState(promotion, exportStart, exportEnd, freshnessLoading)
  const dailyStates = [orderState, afterSalesState, promotionState]
  const readyCount = dailyStates.filter((state) => state.kind === 'ready').length
  const pendingCount = dailyStates.filter((state) => !['ready', 'loading'].includes(state.kind)).length
  const stateGapText = (state: UpdateState) => state.gapDays === null ? '—' : `${state.gapDays}天`
  const heroTitle = freshnessLoading && !freshness
    ? '正在核对今天的数据'
    : pendingCount === 0 ? '今天的数据已经齐全' : `今天还差 ${pendingCount} 组数据`

  return (
    <div className="page-stack pdd-page">
      <div className="page-title-row">
        <div>
          <span className="pdd-platform-label">PINDUODUO</span>
          <h1>数据更新</h1>
          <p>按清单从后台导出原文件，完成后直接交给 Codex；商品主档仅在资料变化后更新。</p>
        </div>
        <div className="page-title-actions">
          <button type="button" className="text-link upload-refresh" onClick={() => void loadFreshness()} disabled={freshnessLoading}>
            <RefreshCw size={14} /> 刷新状态
          </button>
        </div>
      </div>

      {freshnessError && <div className="form-error">{freshnessError}<button type="button" className="text-link" onClick={() => void loadFreshness()}>重试</button></div>}

      <section className="overview-section pdd-update-hero">
        <div className="pdd-update-summary">
          <div>
            <span className="pdd-update-eyebrow">今日更新 · {today}</span>
            <h2>{heroTitle}</h2>
            <p>每日检查订单、售后和推广是否完整覆盖至昨天；完成后把后台原文件直接交给 Codex。</p>
          </div>
          <div className="pdd-update-summary-metrics">
            <div><span>目标日期</span><strong>{exportEnd}</strong></div>
            <div><span>统一90天范围</span><strong>{exportStart} — {exportEnd}</strong></div>
            <div><span>当前进度</span><strong>{readyCount} / 3 组已覆盖</strong></div>
          </div>
        </div>

        <div className="pdd-update-workspace">
          <div className="pdd-update-task-list">
            <div className="pdd-update-block-title">
              <div><h3>今日任务</h3><p>经营数据统一导出 {exportRange}</p></div>
              <span>每日3组 · 共4个文件</span>
            </div>
            <UpdateTaskRow
              index={1}
              title="订单导出"
              fileLabel="1份 CSV"
              route="订单查询 → 全部订单"
              dateRule="日期字段：订单下单时间"
              range={exportRange}
              state={orderState}
            />
            <UpdateTaskRow
              index={2}
              title="售后工作台导出"
              fileLabel="1份 XLSX"
              route="售后工作台 → 售后单导出"
              dateRule="日期字段：申请时间"
              range={exportRange}
              state={afterSalesState}
            />
            <UpdateTaskRow
              index={3}
              title="推广报表"
              fileLabel="2份 XLS / XLSX"
              route="推广报表 → 商品推广"
              dateRule="分天数据＋商品汇总数据；分天若多带开头几天可保留"
              range={`${exportStart} — ${exportEnd}`}
              state={promotionState}
            />
          </div>
          <aside className="pdd-update-handoff">
            <span className="pdd-update-handoff-icon"><Clock3 size={18} aria-hidden /></span>
            <h3>本次交给 Codex</h3>
            <ol>
              <li>订单导出 CSV</li>
              <li>售后工作台 XLSX</li>
              <li>分天报表</li>
              <li>商品汇总报表</li>
            </ol>
            <p>保留后台原始文件名，不要压缩、改名或转换格式。</p>
          </aside>
        </div>

        <div className="pdd-update-on-demand">
          <span className="pdd-update-task-index is-on-demand">4</span>
          <div>
            <div className="pdd-update-task-title"><strong>商品主档</strong><span>按需 · 1份 CSV</span></div>
            <p>妙手搬家 → 全店商品导出 → SKU导出；上新、下架或SKU资料变化后提供，不设日期范围。</p>
          </div>
          <div className="pdd-update-on-demand-meta">
            <span>{catalog?.exportedAt ? `最近导出 ${exportTimeText(catalog.exportedAt).slice(0, 10)}` : '尚无主档'}</span>
            <UpdateStatusPill state={{ kind: 'on-demand', label: '按需维护', gapDays: null }} />
          </div>
        </div>
      </section>

      <section className="overview-section pdd-update-status-section">
        <div className="pdd-update-block-title">
          <div>
            <h2>当前数据状态</h2>
            <p>{freshness ? `状态生成于 ${exportTimeText(freshness.generatedAt)}` : '正在加载数据状态…'}</p>
          </div>
        </div>
        <div className="pdd-update-table-wrap">
          <table className="pdd-update-table">
            <thead><tr><th>数据</th><th>应覆盖至</th><th>当前覆盖</th><th>差距</th><th>最近处理</th><th>状态</th></tr></thead>
            <tbody>
              <tr>
                <td><strong>订单事实</strong><small>1份 CSV · {orders?.sourceRows?.toLocaleString('zh-CN') || '—'} 行</small></td>
                <td>{exportEnd}</td>
                <td><strong>{orders?.queryEnd || '—'}</strong><small>{orders?.queryStart ? `${orders.queryStart} 起` : '尚无数据'}</small></td>
                <td>{stateGapText(orderState)}</td>
                <td>{exportTimeText(orders?.exportedAt)}</td>
                <td><UpdateStatusPill state={orderState} /></td>
              </tr>
              <tr>
                <td><strong>售后事实</strong><small>1份 XLSX · {afterSales?.sourceRows?.toLocaleString('zh-CN') || '—'} 行</small></td>
                <td>{exportEnd}</td>
                <td><strong>{afterSales?.queryEnd || '—'}</strong><small>{afterSales?.queryStart ? `${afterSales.queryStart} 起` : '尚无数据'}</small></td>
                <td>{stateGapText(afterSalesState)}</td>
                <td>{exportTimeText(afterSales?.exportedAt)}</td>
                <td><UpdateStatusPill state={afterSalesState} /></td>
              </tr>
              <tr>
                <td><strong>推广报表</strong><small>2份 XLS / XLSX</small></td>
                <td>{exportEnd}</td>
                <td><strong>{promotion?.queryEnd || '—'}</strong><small>{advertisingCoveredUntil ? `停投证据使广告口径覆盖至 ${advertisingCoveredUntil}` : '暂无停投补充证据'}</small></td>
                <td>{stateGapText(promotionState)}</td>
                <td>{exportTimeText(promotion?.generatedAt)}</td>
                <td><UpdateStatusPill state={promotionState} /></td>
              </tr>
              <tr className="is-on-demand-row">
                <td><strong>商品主档</strong><small>妙手SKU CSV · 按需</small></td>
                <td>资料变化后</td>
                <td><strong>{catalog?.exportedAt ? exportTimeText(catalog.exportedAt).slice(0, 10) : '—'}</strong><small>{catalog ? `${catalog.productCount?.toLocaleString('zh-CN')} 商品 / ${catalog.variantCount?.toLocaleString('zh-CN')} SKU` : '尚无主档'}</small></td>
                <td>—</td>
                <td>{exportTimeText(catalog?.exportedAt)}</td>
                <td><UpdateStatusPill state={{ kind: 'on-demand', label: '按需维护', gapDays: null }} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <details className="overview-section pdd-update-details">
        <summary><span>导出路径与口径说明</span><ChevronDown size={16} aria-hidden /></summary>
        <div className="pdd-update-detail-grid">
          <article><h3>订单</h3><p>订单查询 → 全部订单；按“订单下单时间”导出完整90天CSV。订单状态会回补，新文件覆盖重叠日期。</p></article>
          <article><h3>售后</h3><p>售后工作台按“申请时间”导出XLSX；处理时复用同批订单CSV生成支付队列二退率，无需重复导出订单。</p></article>
          <article><h3>推广</h3><p>同一范围分别导出账户分天和商品汇总两份报表；停投期间0消耗也要导出，缺失不能当作0。</p></article>
          <article><h3>商品主档</h3><p>妙手搬家 → 全店商品导出 → SKU导出；只在上新、下架或资料变化后更新，不设日期范围。</p></article>
        </div>
        <div className="pdd-update-privacy"><CircleAlert size={16} aria-hidden />订单和售后原文件含个人信息，仅在本机临时处理；所有文件必须保留后台原始名称。</div>
      </details>
    </div>
  )
}
